import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { el } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Large Custom Date Picker Component
 * Features:
 * - Πεδίο χειροκίνητης καταχώρησης ημερομηνίας
 * - Μεγάλο calendar popup (400x380px)
 * - Ελληνική γλώσσα
 * - Ευανάγνωστο styling
 */
const LargeDatePicker = ({ 
  value, 
  onChange, 
  id, 
  placeholder = "Επιλέξτε ημερομηνία",
  className = "",
  'data-testid': dataTestId,
  ...props 
}) => {
  // Convert string value to Date object
  const selectedDate = value ? new Date(value) : null;
  
  // Manual input state
  const [manualInput, setManualInput] = useState('');
  
  // Sync manual input with selected date
  useEffect(() => {
    if (selectedDate) {
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      setManualInput(`${day}/${month}/${year}`);
    } else {
      setManualInput('');
    }
  }, [value]);

  const handleChange = (date) => {
    if (date) {
      // Convert Date to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      // Create synthetic event to match native input behavior
      const syntheticEvent = {
        target: {
          value: formattedDate,
          id: id
        }
      };
      onChange(syntheticEvent);
    } else {
      onChange({ target: { value: '', id: id } });
    }
  };
  
  // Handle manual input change
  const handleManualInputChange = (e) => {
    const inputValue = e.target.value;
    setManualInput(inputValue);
    
    // Try to parse the date (format: dd/mm/yyyy)
    const parts = inputValue.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          handleChange(date);
        }
      }
    }
  };

  return (
    <div className="space-y-1" data-testid={dataTestId}>
      {/* Manual input field */}
      <input
        type="text"
        value={manualInput}
        onChange={handleManualInputChange}
        placeholder="ηη/μμ/εεεε"
        data-testid={dataTestId ? `${dataTestId}-manual` : undefined}
        className="w-full h-8 px-3 py-1 border border-slate-300 rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-0 focus:border-slate-400"
        name={`dp-${Math.random().toString(36).slice(2, 10)}`}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        aria-autocomplete="none"
      />
      {/* Calendar picker - centered */}
      <div className="flex justify-center">
        <DatePicker
          id={id}
          selected={selectedDate}
          onChange={handleChange}
          dateFormat="dd/MM/yyyy"
          locale={el}
          placeholderText={placeholder}
          className={`w-full h-9 px-3 py-1 border border-slate-300 rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-0 focus:border-slate-400 ${className}`}
          calendarClassName="small-calendar"
          showPopperArrow={false}
          autoComplete="off"
          fixedHeight
          inline
          {...props}
        />
      </div>
    </div>
  );
};

export default LargeDatePicker;
